/**
 * All landing-page copy lives here.
 *
 * SOURCE OF TRUTH: figures and product capabilities are taken from the live
 * qost.ai site (home, /pricing, /about, /contact, /docs). Numbers like "250+",
 * "$200M+" and the EasyFiling case study are Qost's own published claims, carried
 * over verbatim - they are NOT invented here. If any of them changes, update the
 * value in this file rather than in the JSX.
 *
 * POSITIONING: this site deliberately promotes Qost as a FinOps and cloud cost
 * VISIBILITY platform, not as a cost-cutting tool. Savings appear as an outcome
 * of visibility, never as the headline promise. Keep that framing when editing:
 * lead with seeing, allocating and forecasting spend; let savings follow.
 */

export const site = {
  name: "Qost.ai",
  wordmark: "Qost",
  tagline: "Cloud FinOps and cost visibility for modern engineering teams.",
  headline: "See exactly where your AWS spend goes",
  // Real URLs from the live site.
  loginUrl: "https://app.qost.ai/login",
  registerUrl: "https://app.qost.ai/register",
  docsUrl: "https://qost.ai/docs",
  apiReferenceUrl: "https://qost.ai/api-reference",
  pricingUrl: "https://qost.ai/pricing",
  aboutUrl: "https://qost.ai/about",
  contactUrl: "https://qost.ai/contact",
  blogUrl: "https://qost.ai/blog",
  privacyUrl: "https://qost.ai/privacy",
  termsUrl: "https://qost.ai/terms",
  cloudlayaUrl: "https://www.cloudlaya.com/",
  email: "hello@qost.ai",
  whatsapp: "+977 970-918-8309",
  whatsappUrl: "https://wa.me/9779709188309",
  headquarters: "Kathmandu, Nepal",
  businessHours: "Mon – Fri, 9am – 6pm NPT",
};

/**
 * Primary navigation for the multi-page site. Home-page sections are referenced
 * as "/#anchor" so the same links work from any page.
 */
export const nav = [
  { label: "Features", href: "/#features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Documentation", href: "/docs" },
  { label: "About", href: "/about" },
];

export const hero = {
  eyebrow: "Cloud FinOps & Cost Visibility",
  heading: site.headline,
  subheading:
    "Qost.ai gives engineering, finance and leadership one shared view of AWS spend — usage broken down by service, account and organization, allocated to the teams who own it, and forecast before the invoice lands.",
  primaryCta: { label: "Get Started for Free", href: site.registerUrl },
  secondaryCta: { label: "Book a walkthrough", href: "/#contact" },
  trustLine: "No credit card required • Read-only access • Cancel anytime",
  stat: { label: "Savings identified", value: "$12,450/mo" },
};

/** Certification / partnership badges shown on the live site. */
export const trustBadges = [
  "SOC 2 Type II Certified",
  "Read-Only Access",
  "AWS Advanced Partner",
  "ISO 27001 Compliant",
  "250+ Enterprise Clients",
];

/** Partner names, rendered as text - see README about adding real logo files. */
export const partners = {
  caption: "Our strategic partners & affiliations",
  names: ["AWS Partner", "Microsoft", "Google Cloud Partner"],
};

export const features = {
  eyebrow: "Visibility & intelligence",
  heading: "Complete Cloud Cost Intelligence",
  subheading:
    "One source of truth for AWS spend across every account and organization — the foundation any FinOps practice is built on.",
  items: [
    {
      title: "AI-Driven Cost Analysis",
      body: "Our ML models analyze your usage patterns 24/7 to uncover hidden savings opportunities others miss.",
      icon: "chart",
    },
    {
      title: "Real-Time Monitoring",
      body: "Get instant alerts on anomaly spending. Never wake up to a surprise cloud bill again.",
      icon: "forecast",
    },
    {
      title: "Smart Rightsizing",
      body: "Automatically identify over-provisioned resources and downgrade them without impacting performance.",
      icon: "savings",
    },
    {
      title: "Pricing Optimization",
      body: "We navigate Reserved Instances and Savings Plans for you to lock in the lowest rates.",
      icon: "report",
    },
    {
      title: "Group Buying Power",
      body: "Join our collective to access enterprise-tier volume discounts regardless of your size.",
      icon: "org",
    },
    {
      title: "Read-Only Security",
      body: "We operate with strictly read-only permissions. Your infrastructure remains completely secure.",
      icon: "shield",
    },
  ],
};

export const howItWorks = {
  eyebrow: "How it works",
  heading: "From no visibility to full clarity in 48 hours",
  subheading:
    "No agents, no changes to your workloads, and nothing deployed inside your accounts.",
  steps: [
    {
      step: "01",
      title: "Connect your AWS account",
      body: "Grant read-only access to your billing and usage data. Qost.ai can never create, modify or delete any of your infrastructure.",
    },
    {
      step: "02",
      title: "AI analyses your spend 24/7",
      body: "Usage patterns are analysed continuously across every connected account to surface waste and hidden savings.",
    },
    {
      step: "03",
      title: "Get actionable recommendations",
      body: "Most teams receive their first actionable recommendations within 48 hours, with the estimated saving attached to each one.",
    },
    {
      step: "04",
      title: "Approve, then watch it land",
      body: "You approve every change. Savings are tracked against the baseline you started from so the impact is visible.",
    },
  ],
};

/** Real case study from the live site - not a placeholder. */
export const caseStudy = {
  eyebrow: "Customer outcome",
  heading: "How visibility changed EasyFiling's AWS spend",
  body: "EasyFiling — a Cloudlaya-backed SaaS — was overspending on unoptimized EC2 instances and unused storage. Within 48 hours of connecting Qost.ai, they identified and realized ~$72,000/month in savings.",
  bullets: [
    "Instant discovery of zombie resources",
    "Automated spot instance orchestration",
    "Zero downtime implementation",
  ],
  stat: { label: "Monthly savings", value: "+$72,000" },
  cta: { label: "Talk to us about your estate", href: "/#contact" },
  // Months shown on the live savings chart.
  chartMonths: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
};

export const about = {
  eyebrow: "About",
  heading: "The cloud experience behind every recommendation",
  body: "Qost.ai is built by Cloudlaya — a leading AWS Advanced Partner and enterprise cloud provider trusted by 250+ businesses. Every optimization recommendation in Qost.ai is grounded in real-world cloud architecture knowledge from Cloudlaya's certified engineers.",
  stats: [
    { value: "250+", label: "Enterprise clients" },
    { value: "$200M+", label: "Savings identified" },
    { value: "30%", label: "Average cost reduction" },
    { value: "48hrs", label: "Time to first insight" },
  ],
  servicesIntro:
    "Cloudlaya's full suite of enterprise cloud services powers the expertise embedded in every Qost.ai recommendation.",
  services: [
    "AWS Managed Services",
    "Cloud Migration",
    "Cloud Consulting",
    "Security & Compliance",
    "DevOps as a Service",
    "24/7 Expert Support",
  ],
  cta: { label: "Visit Cloudlaya.com", href: "https://www.cloudlaya.com/" },

  // --- Content used by the dedicated /about page ---
  pageTitle: "Born from real cloud experience, built for savings",
  pageIntro:
    "Qost.ai is a product of Cloudlaya — a leading cloud services company trusted by 250+ enterprises worldwide. We built Qost.ai because we saw firsthand how much money businesses leave on the table every month.",
  cloudlayaHeading: "Backed by a decade of cloud expertise",
  cloudlayaStory: [
    "Cloudlaya is a premier cloud services provider specializing in AWS Managed Services, Cloud Consulting, Migration, DevOps, and Digital Transformation for enterprises across the globe. As an AWS partner, Cloudlaya has guided hundreds of businesses through every stage of their cloud journey.",
    "After years of managing enterprise cloud infrastructure and seeing clients overpay on AWS month after month, the Cloudlaya team built Qost.ai — an AI-powered layer on top of that deep operational expertise, making cost optimization accessible, automated, and effortless.",
  ],
  missionHeading: "Our Mission",
  mission: [
    "We're on a mission to make cloud cost optimization effortless for every engineering team. Too many organizations waste hours manually sifting through billing dashboards, only to miss the biggest savings hiding in plain sight.",
    "Qost.ai takes Cloudlaya's decade of hands-on cloud expertise and wraps it in an AI engine that works 24/7 — finding savings, flagging waste, and giving your team the confidence to act without ever touching your infrastructure.",
  ],
  valuesHeading: "Our Values",
  valuesIntro: "The principles that guide everything we build at Qost.ai.",
  values: [
    {
      title: "Customer Obsessed",
      body: "Every decision we make starts with a simple question: does this help our customers save more? We've helped 250+ enterprises cut cloud waste.",
    },
    {
      title: "Radical Transparency",
      body: "We show you exactly how we find savings. No black boxes, no hidden fees, no surprises — just clear, actionable recommendations.",
    },
    {
      title: "Trust First",
      body: "Read-only access by design. We will never create, modify, or delete any of your infrastructure without your explicit approval.",
    },
  ],
  partnerHeading: "The cloud partner behind Qost.ai",
};

/**
 * Real pricing from qost.ai/pricing.
 *
 * `monthly` is the published monthly rate. The annual figure is DERIVED from the
 * "Save 20%" claim on that page (20% off monthly); if your billing system ever
 * quotes something different, put the exact annual number here instead of
 * letting it be computed.
 */
export const pricing = {
  eyebrow: "Pricing",
  heading: "Simple, transparent pricing",
  subheading:
    "Start free, scale as you grow. Only pay when Qost.ai delivers real savings to your bottom line.",
  annualNote: "Save 20%",
  annualDiscount: 0.2,
  tiers: [
    {
      name: "Starter",
      price: "Free",
      monthly: null as number | null,
      cadence: "",
      blurb: "Perfect for small teams just getting started with cloud cost optimization.",
      cta: { label: "Get Started Free", href: site.registerUrl },
      features: [
        "Up to $10k monthly AWS spend",
        "Basic cost analysis",
        "Weekly savings reports",
        "Email support",
        "1 AWS account",
      ],
      highlighted: false,
    },
    {
      name: "Pro",
      price: "$499",
      monthly: 499,
      cadence: "/month",
      blurb: "For growing teams that need deeper insights and real-time optimization.",
      cta: { label: "Start Free Trial", href: site.registerUrl },
      features: [
        "Up to $500k monthly AWS spend",
        "AI-driven cost analysis",
        "Daily savings reports",
        "Priority support",
        "Up to 10 AWS accounts",
        "Real-time monitoring & alerts",
        "Custom recommendations",
        "Dedicated account manager",
      ],
      highlighted: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      monthly: null as number | null,
      cadence: "",
      blurb: "For large organizations with complex multi-account infrastructure.",
      cta: { label: "Talk to Sales", href: "/#contact" },
      features: [
        "Unlimited AWS spend",
        "Advanced AI optimization",
        "Real-time reports & dashboards",
        "24/7 dedicated support",
        "Unlimited AWS accounts",
        "Real-time monitoring & alerts",
        "Custom recommendations",
        "Dedicated account manager",
      ],
      highlighted: false,
    },
  ],
  footnotes: [
    "SOC 2 Type II",
    "Read-Only Access",
    "No Credit Card for Free Plan",
    "Cancel Anytime",
  ],
  stillHaveQuestions: {
    heading: "Still have questions?",
    body: "Our Cloudlaya-backed team is ready to walk you through a personalized demo and find the right plan for your team.",
    primaryCta: { label: "Contact Sales", href: "/contact" },
    secondaryCta: { label: "Back to Home", href: "/" },
  },
};

export const faqSection = {
  eyebrow: "FAQ",
  heading: "Frequently asked questions",
  subheading: "Everything you need to know about our pricing.",
  ctaLabel: "Still have questions?",
};

/** Real FAQs from qost.ai/pricing. */
export const faqs = [
  {
    q: "How does the free plan work?",
    a: "The Starter plan is completely free. Simply connect your AWS account with read-only access and start seeing savings recommendations within 48 hours. No credit card required.",
  },
  {
    q: "Can I switch plans at any time?",
    a: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle. We prorate any adjustments.",
  },
  {
    q: "What does read-only access mean?",
    a: "Qost.ai only requires read-only permissions to your AWS billing and usage data. We can never create, modify, or delete any of your infrastructure resources.",
  },
  {
    q: "How quickly will I see savings?",
    a: "Most teams receive their first actionable savings recommendations within 48 hours of connecting their AWS account. Average customers save 30% on their AWS bill.",
  },
  {
    q: "Do you support multi-cloud?",
    a: "Currently we specialize in AWS optimization as an AWS Advanced Partner. Support for Azure and GCP is on our roadmap for 2026.",
  },
  {
    q: "Is there a contract or commitment?",
    a: "No long-term contracts. The Pro plan is billed monthly and you can cancel at any time. Enterprise plans have custom terms based on your needs.",
  },
];

export const contact = {
  eyebrow: "Get in touch",
  heading: "Let's talk about your cloud savings",
  subheading:
    "Whether you have questions about Qost.ai, want a custom demo, or need to reach the Cloudlaya team — we're here to help.",
  formHeading: "Send us a message",
  formSubheading: "Fill out the form and we'll get back to you shortly.",
  emailNote: "We respond within 24 hours",
  hqNote: "Cloudlaya HQ — serving clients globally",
  hoursNote: "Weekend support for Enterprise plans",
  fields: {
    name: "Full name",
    email: "Work email",
    company: "Company",
    message: "What would you like to solve?",
  },
  submitLabel: "Send message",
};

export const blogPage = {
  eyebrow: "Insights & Updates",
  title: "The Qost.ai Blog",
  intro:
    "Cloud FinOps practices, cost visibility, product updates, and engineering insights from the Qost.ai team.",
  // The live site is in exactly this state today: no posts published yet.
  emptyState: "No posts yet. Check back soon!",
  notifyCta: { label: "Get notified", href: "https://app.qost.ai/register" },
};

/**
 * FinOps practice section.
 *
 * Every capability listed here is taken from the live qost.ai/docs page (the
 * Cost Analysis, Alerts & Monitoring and Account & Settings topics) - nothing in
 * this block is a new claim about the product.
 */
export const finops = {
  eyebrow: "Built for FinOps",
  heading: "Visibility first. Optimization follows.",
  intro:
    "You cannot govern what you cannot see. Qost.ai gives engineering, finance and leadership the same view of cloud spend, so decisions stop being made on last month's estimate.",
  pillars: [
    {
      step: "01",
      title: "See it",
      body: "Every account and organization in one place, with spend broken down by service and tracked over time.",
      items: ["Cost breakdown by service", "Historical trends", "Multi-account rollup"],
    },
    {
      step: "02",
      title: "Share it",
      body: "Give each stakeholder the view they need - finance, engineering leads, and the teams who own the workload.",
      items: ["Reports and dashboards", "CSV and PDF export", "Slack and email delivery"],
    },
    {
      step: "03",
      title: "Act on it",
      body: "Catch drift while it is still small, and route the signal to whoever can fix it.",
      items: ["Anomaly detection", "Spend threshold alerts", "Custom alert rules"],
    },
  ],
};

export const finalCta = {
  heading: "Ready to see where your cloud spend actually goes?",
  body: "Join Cloudlaya's enterprise network. Get your free AWS spend analysis today — no credit card, no commitment.",
  primaryCta: { label: "Get Started for Free", href: site.registerUrl },
  secondaryCta: { label: "Talk to Sales", href: "/#contact" },
};

export const footer = {
  blurb: site.tagline + " One shared view of what your cloud actually costs.",
  columns: [
    {
      title: "Product",
      links: [
        { label: "Features", href: "/#features" },
        { label: "Pricing", href: "/pricing" },
        { label: "Case study", href: "/#case-study" },
        // These four routes have no content of their own yet - see ComingSoonPage.
        { label: "Changelog", href: "/changelog" },
        { label: "Integrations", href: "/integrations" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Documentation", href: "/docs" },
        { label: "API Reference", href: "/api-reference" },
        { label: "Blog", href: "/blog" },
        { label: "Community", href: "/community" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Careers", href: "/careers" },
        { label: "Contact", href: "/contact" },
        { label: "Cloudlaya.com", href: site.cloudlayaUrl },
      ],
    },
  ],
};
